import { Map, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProperties } from '@providers';
import { MapCircle, MapMarker, MapPolygon } from '@shared-components';
import { getPointsBounds } from '@utils';

const mapId = import.meta.env.VITE_FRONT_PAGE_MAP_ID;
const propertyDisplayStyle: 'circle' | 'marker' = 'circle';
const propertyDisplayOnFullMap: 'always' | 'outOfArea' = 'always';

export type AreasMapProps = {
  area: string;
  setArea: (area: string) => void;
};

type AreaBounds = {
  name: string;
  paths: google.maps.LatLngLiteral[];
};

type MapCamera = {
  center: google.maps.LatLngLiteral;
  zoom: number;
};

export const AreasMap: FC<AreasMapProps> = (props) => {
  const isLoaded = useApiIsLoaded();
  const propertiesModel = useProperties();
  const navigate = useNavigate();

  const [areaBounds, setAreaBounds] = useState<AreaBounds[]>([]);
  const [hoverArea, setHoverArea] = useState<string | undefined>(undefined);
  const [hoverProperty, setHoverProperty] = useState<number | undefined>(
    undefined,
  );
  const [mapCamera, setMapCamera] = useState<MapCamera>({
    center: { lat: 38.0947911, lng: 23.5440415 },
    zoom: 10,
  });

  useEffect(() => {
    if (!isLoaded) return;

    const getAreaBounds = (areaName: string) => {
      const geometry = propertiesModel.geometries.find(
        (geom) =>
          geom.name.toLowerCase() === areaName.toLowerCase() ||
          geom.alt_name?.toLowerCase() === areaName.toLowerCase(),
      );
      const paths =
        geometry?.coordinates?.[0].map(
          (coords) =>
            ({ lat: coords[1], lng: coords[0] }) as google.maps.LatLngLiteral,
        ) || [];

      return { name: areaName, paths };
    };

    const areasToDraw = props.area
      ? [props.area]
      : propertiesModel.allAreasWithProperties;
    const bounds = areasToDraw.map(getAreaBounds);
    setAreaBounds(bounds);

    const allPoints = bounds.flatMap((b) => b.paths);
    // No geometry matched (e.g. no area names for this language): empty bounds
    // have no center, so keep the current camera
    if (!allPoints.length) return;

    const allBounds = getPointsBounds(allPoints);
    const center = allBounds.getCenter().toJSON();
    const span = allBounds.toSpan();

    const latLonRatio = 1.264;
    const lonToMeters = 87843;
    const displayFactor = 0.0006;
    const metersSpan =
      Math.max(span.lat() * latLonRatio, span.lng()) * lonToMeters;
    const zoom = 14 - Math.log2(metersSpan * displayFactor) / Math.log2(2.5);

    setMapCamera({ center, zoom });
  }, [
    props.area,
    propertiesModel.geometries,
    propertiesModel.allAreasWithProperties,
  ]);

  const allDrawableAreas = useMemo(
    () => new Set(areaBounds.filter((b) => b.paths.length).map((b) => b.name)),
    [areaBounds],
  );

  return (
    <Map
      style={{
        width: '100%',
        height: '60vh',
      }}
      center={mapCamera.center}
      disableDefaultUI={true}
      gestureHandling='none'
      keyboardShortcuts={false}
      mapId={mapId}
      zoom={mapCamera.zoom}
    >
      {areaBounds.map((area, index) => {
        const isSelected = props.area === area.name;
        const isHovered = hoverArea === area.name;

        return (
          <MapPolygon
            key={index}
            paths={area.paths}
            fillColor={isHovered ? '#1095bd' : '#16b9eb'}
            fillOpacity={isSelected ? 0.1 : 0.35}
            // FIXME See issue in polygon
            //label={propertiesModel.properties.filter(p => p.area === area.name).length.toString()}
            onClick={() => props.setArea(area.name)}
            onMouseOver={() => setHoverArea(area.name)}
            onMouseOut={() =>
              hoverArea === area.name && setHoverArea(undefined)
            }
            strokeColor={isHovered ? '#1095bd' : '#16b9eb'}
            strokeWeight={2}
          />
        );
      })}
      {propertiesModel.properties
        .filter(
          (prop) =>
            props.area ||
            propertyDisplayOnFullMap === 'always' ||
            (propertyDisplayOnFullMap === 'outOfAreas' &&
              !allDrawableAreas.has(prop.area)),
        )
        .map((prop) => {
          const isHovered = hoverProperty === prop.id;

          if (propertyDisplayStyle === 'circle') {
            return (
              <MapCircle
                key={prop.id}
                center={{ lat: prop.latitude, lng: prop.longitude }}
                radius={400}
                fillColor={isHovered ? '#1095bd' : '#16b9eb'}
                fillOpacity={0.35}
                strokeColor={isHovered ? '#1095bd' : '#16b9eb'}
                strokeOpacity={0.8}
                strokeWeight={2}
                clickable={true}
                draggable={false}
                editable={false}
                visible={true}
                onClick={() => navigate(`/properties/${prop.id}`)}
                onMouseOver={() => setHoverProperty(prop.id)}
                onMouseOut={() =>
                  hoverProperty === prop.id && setHoverProperty(undefined)
                }
                zIndex={10}
              />
            );
          } else {
            return (
              <MapMarker
                key={prop.id}
                position={{ lat: prop.latitude, lng: prop.longitude }}
                pinBackground='#16b9eb'
                pinBorderColor='#1095bd'
                pinGlyphColor='#ffffff'
                zIndex={10}
              />
            );
          }
        })}
    </Map>
  );
};
